// Reading a driver license at the counter.
//
// A 2D USB barcode scanner (Zebra DS2208, Honeywell 1470g...) types the barcode on the back of
// the license like a keyboard, very fast. Its line breaks come in as Enter, or as Ctrl+J / Ctrl+M
// (Ctrl+J would open Chrome's downloads), and its separators as Ctrl+^. The camera reads the same
// barcode with the browser's own barcode reader, where the device has one (Chrome on Android and
// on Macs).
import { useEffect, useRef } from 'react';
import { parseLicense, type LicenseData } from '../../shared/license';

/** The character a scanner meant with one key press; null for keys that aren't part of a barcode (Shift and so on). */
export function scannerChar(e: KeyboardEvent): string | null {
  if (e.metaKey) return null;
  // AltGr (Ctrl+Alt) types characters like "@" on some keyboards.
  if (e.ctrlKey && e.altKey) return e.key.length === 1 ? e.key : null;
  if (e.ctrlKey) {
    const k = e.key.toLowerCase();
    if (k === 'j' || k === 'm' || e.code === 'KeyJ' || e.code === 'KeyM') return '\n';
    if (k === '^' || k === '6' || k === '~' || e.code === 'Digit6') return '\x1e';
    return '';
  }
  if (e.altKey) return null;
  if (e.key === 'Enter') return '\n';
  return e.key.length === 1 ? e.key : null;
}

function restoreValue(el: HTMLInputElement | HTMLTextAreaElement, value: string) {
  if (!el.isConnected || el.value === value) return;
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

const HEADER = /ANSI ?\d{6}|AAMVA\d{6}/;

/**
 * Lets staff scan a license any time on a screen, without pressing anything first. A burst of
 * very fast keys starting with "@" that has "ANSI" in it is a license, not typing: its keys are
 * kept from the page (Enter doesn't submit, Ctrl+J doesn't open downloads), the box that had the
 * cursor gets its text back, and the license goes to onScan. Scanning an IMEI or a product
 * barcode is left alone.
 */
export function useLicenseScanner(onScan: (d: LicenseData) => void, onFail: () => void, enabled = true) {
  const scan = useRef(onScan);
  const fail = useRef(onFail);
  scan.current = onScan;
  fail.current = onFail;
  useEffect(() => {
    if (!enabled) return;
    let buf = '';
    let last = 0;
    let mode: 'idle' | 'maybe' | 'scan' = 'idle';
    let timer: ReturnType<typeof setTimeout> | undefined;
    let snap: { el: HTMLInputElement | HTMLTextAreaElement; value: string } | null = null;
    const reset = () => {
      buf = '';
      mode = 'idle';
      snap = null;
    };
    const finish = () => {
      const text = buf;
      const s = snap;
      const was = mode;
      reset();
      if (was !== 'scan') return;
      if (s) restoreValue(s.el, s.value);
      const d = parseLicense(text);
      if (d) scan.current(d);
      else fail.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing) return;
      const now = performance.now();
      const gap = now - last;
      last = now;
      const ch = scannerChar(e);
      if (mode === 'scan') {
        e.preventDefault();
        e.stopPropagation();
        if (ch) buf += ch;
        clearTimeout(timer);
        timer = setTimeout(finish, 300);
        return;
      }
      if (ch === null) return;
      if (mode === 'idle' || gap > 80) {
        // A new burst of keys: remember the box with the cursor, in case it's a scanner.
        reset();
        const a = document.activeElement;
        snap = a instanceof HTMLInputElement || a instanceof HTMLTextAreaElement ? { el: a, value: a.value } : null;
        buf = ch;
        mode = 'maybe';
      } else {
        buf += ch;
      }
      const scanner = buf[0] === '@' || buf.startsWith(']L');
      if (scanner && buf.length > 1 && (ch === '\n' || ch === '\x1e' || ch === '')) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (scanner && HEADER.test(buf)) {
        mode = 'scan';
        e.preventDefault();
        e.stopPropagation();
      } else if (buf.length > 40) {
        mode = 'idle';
        buf = '';
      }
      clearTimeout(timer);
      timer = setTimeout(mode === 'scan' ? finish : reset, mode === 'scan' ? 300 : 120);
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      clearTimeout(timer);
    };
  }, [enabled]);
}

/** True when this browser can read a license barcode with the camera (it has a PDF417 barcode reader). */
export async function cameraReadsLicenses(): Promise<boolean> {
  const BD = (window as any).BarcodeDetector;
  if (!BD || !navigator.mediaDevices?.getUserMedia) return false;
  try {
    const formats: string[] = await BD.getSupportedFormats();
    return formats.includes('pdf417');
  } catch {
    return false;
  }
}
